import { helpers } from "../../../ui/util/helpers.ts";
import { formatSeasonRecord } from "../../../ui/util/seasonRecord.ts";
import type { ActualProps } from "./index.tsx";
import { AWARD_NAMES } from "../../util/awardNames.ts";
import {
  translateTeamRegion,
  translateTeamName,
} from "../../util/teamNames.ts";
import i18n from "../../util/i18n.ts";
import { TeamAbbrevText } from "../../components/TeamAbbrevLink.tsx";
import { translateConfName } from "../../util/confDivNames.ts";
import { formatPlayerNameFromLegacyName } from "../../util/playerNames.ts";
import { getCol } from "../../util/getCol.ts";
import { createMobileReturnUrl } from "../../util/mobilePageContext.ts";

const getMobileScrollY = () =>
  typeof window !== "undefined" ? window.scrollY : undefined;

const createHistoryPlayerHref = (pid: number, season: number) =>
  createMobileReturnUrl(helpers.leagueUrl(["player", pid]), {
    activeTab: String(season),
    filterKey: "historySeason",
    filterValue: season,
    pageID: "history",
    scrollY: getMobileScrollY(),
  });

const createHistoryPlayoffsHref = (season: number) =>
  createMobileReturnUrl(helpers.leagueUrl(["playoffs", season]), {
    archiveSeason: season,
    captureCurrentReturnUrl: false,
    originSection: "league",
    pageID: "history",
    readonlyReason: "historical-playoffs",
    returnHref: helpers.leagueUrl(["history", season]),
    returnLabel: i18n.t("history.seasonSummary", {
      ns: "ui",
      defaultValue: "View season summary",
    }),
    sourceLabel: i18n.t("history.title", {
      ns: "ui",
      defaultValue: "Season Summary",
    }),
    sourcePage: "history",
  });

const Winner = ({
  award,
  defense = false,
  finals = false,
  season,
  userTid,
}: {
  award: ActualProps["awards"][number];
  defense?: boolean;
  finals?: boolean;
  season: number;
  userTid: number;
}) => {
  if (!award) {
    const missingLabel = i18n.t("history.mobileAwardMissing", {
      ns: "ui",
      defaultValue: "Not awarded",
    });
    return finals ? missingLabel : <p>{missingLabel}</p>;
  }

  const stats = defense ? ["trb", "blk", "stl"] : ["pts", "trb", "ast"];
  const separator = i18n.language.startsWith("zh") ? "，" : ", ";

  const nameAndStats = (
    <>
      <span className={award.tid === userTid ? "table-info" : undefined}>
        <b>
          <a href={createHistoryPlayerHref(award.pid, season)}>
            {formatPlayerNameFromLegacyName(award.name)}
          </a>
        </b>{" "}
        (
        <a
          href={helpers.leagueUrl([
            "roster",
            `${award.abbrev}_${award.tid}`,
            season,
          ])}
        >
          <TeamAbbrevText tid={award.tid} abbrev={award.abbrev} />
        </a>
        )
      </span>
      <br />
      {stats
        .map(
          (stat) =>
            `${helpers.roundStat(award[stat], stat)} ${getCol(`stat:${stat}`).title}`,
        )
        .join(separator)}
    </>
  );

  return finals ? nameAndStats : <p>{nameAndStats}</p>;
};

const AwardsAndChamp = ({
  awards,
  champ,
  confs,
  season,
  userTid,
}: Pick<ActualProps, "awards" | "champ" | "confs" | "season" | "userTid">) => {
  return (
    <div className="row g-2">
      <section className="col-12">
        <div className="card h-100">
          <div className="card-body py-2">
            <h2 className="h6 mb-2">
              {i18n.t("history.leagueChamps", {
                ns: "ui",
                defaultValue: "League Champs",
              })}
            </h2>
            {champ ? (
              <div>
                <p>
                  <span
                    className={champ.tid === userTid ? "table-info" : undefined}
                  >
                    <b>
                      <a
                        href={helpers.leagueUrl([
                          "roster",
                          `${champ.seasonAttrs.abbrev}_${champ.tid}`,
                          season,
                        ])}
                      >
                        {translateTeamRegion(champ.seasonAttrs.region)}{" "}
                        {translateTeamName(champ.seasonAttrs.name)}
                      </a>
                    </b>
                  </span>
                  <br />
                  <a href={createHistoryPlayoffsHref(season)}>
                    {i18n.t("history.playoffsBracket", {
                      ns: "ui",
                      defaultValue: "Playoffs Bracket",
                    })}
                  </a>
                </p>
                <p>
                  {AWARD_NAMES.finalsMvp}:{" "}
                  <Winner
                    award={awards.finalsMvp}
                    finals
                    season={season}
                    userTid={userTid}
                  />
                </p>
                {awards.sfmvp ? (
                  <div className="mb-3">
                    {i18n.t("history.conferenceFinalsMvps", {
                      ns: "ui",
                      defaultValue: "Conference Finals MVPs",
                    })}
                    :
                    {(awards.sfmvp as any[]).map((p, i) => (
                      <div key={i}>
                        {p ? (
                          <Winner
                            award={p}
                            finals
                            season={season}
                            userTid={userTid}
                          />
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : (
              <p>
                {i18n.t("history.mobileChampionMissing", {
                  ns: "ui",
                  defaultValue: "Champion unavailable",
                })}
              </p>
            )}
          </div>
        </div>
      </section>
      <section className="col-12">
        <div className="card h-100">
          <div className="card-body py-2">
            <h2 className="h6 mb-2">
              {i18n.t("history.bestRecord", {
                ns: "ui",
                defaultValue: "Best Record",
              })}
            </h2>
            {awards.bestRecordConfs
              .filter((a: any) => a !== undefined && a !== null)
              .map((t: any, i: number) =>
                t !== undefined ? (
                  <p key={t.tid}>
                    {translateConfName(confs[i]!.name)}:<br />
                    <span
                      className={t.tid === userTid ? "table-info" : undefined}
                    >
                      <a
                        href={helpers.leagueUrl([
                          "roster",
                          `${t.abbrev}_${t.tid}`,
                          season,
                        ])}
                      >
                        {translateTeamRegion(t.region)}{" "}
                        {translateTeamName(t.name)}
                      </a>{" "}
                      ({formatSeasonRecord(t)})
                    </span>
                    <br />
                  </p>
                ) : null,
              )}
          </div>
        </div>
      </section>
      <section className="col-12 col-sm-6">
        <div className="card h-100">
          <div className="card-body py-2">
            <h2 className="h6 mb-2">{AWARD_NAMES.mvp}</h2>
            <Winner award={awards.mvp} season={season} userTid={userTid} />
          </div>
        </div>
      </section>
      <section className="col-12 col-sm-6">
        <div className="card h-100">
          <div className="card-body py-2">
            <h2 className="h6 mb-2">{AWARD_NAMES.dpoy}</h2>
            <Winner
              award={awards.dpoy}
              season={season}
              userTid={userTid}
              defense
            />
          </div>
        </div>
      </section>
      <section className="col-12 col-sm-6">
        <div className="card h-100">
          <div className="card-body py-2">
            <h2 className="h6 mb-2">{AWARD_NAMES.smoy}</h2>
            <Winner award={awards.smoy} season={season} userTid={userTid} />
          </div>
        </div>
      </section>
      <section className="col-12 col-sm-6">
        <div className="card h-100">
          <div className="card-body py-2">
            <h2 className="h6 mb-2">{AWARD_NAMES.mip}</h2>
            <Winner award={awards.mip} season={season} userTid={userTid} />
          </div>
        </div>
      </section>
      <section className="col-12 col-sm-6">
        <div className="card h-100">
          <div className="card-body py-2">
            <h2 className="h6 mb-2">{AWARD_NAMES.roy}</h2>
            <Winner award={awards.roy} season={season} userTid={userTid} />
          </div>
        </div>
      </section>
    </div>
  );
};

export default AwardsAndChamp;
